import { HateoasResponseSchema, HateoasTarget } from '@repo/contracts';

/**
 * Client HATEOAS d'execution d'action.
 *
 * IMPORTANT: ce module n'est pas responsable de trouver le lien a appeler.
 * Il execute uniquement une action deja resolue (href + method) et applique
 * les conventions de reponse HATEOAS (actions, ui.effects, session, redirect).
 */
interface ExecuteHateoasActionOptions {
    action: HateoasTarget;
    request: (href: string, options?: Omit<RequestInit, 'body'> & { body?: unknown }, returnHeaders?: boolean) => Promise<any>;
    body?: unknown;
    pathParams?: Record<string, string>;
    returnHeaders?: boolean;
    mode?: 'resources' | 'envelope';
    currentPathname?: string;
    onUpdateScope?: (payload: { actions?: Record<string, HateoasTarget>; links?: Record<string, HateoasTarget> }, action: HateoasTarget) => void;
    onRefreshSession?: () => Promise<void> | void;
    onRedirect?: (target: string) => void;
}

function resolveHateoasHrefTemplate(href: string, pathParams?: Record<string, string>): string {
    if (!pathParams || !href) return href;

    let finalHref = href;

    Object.entries(pathParams).forEach(([key, value]) => {
        const encodedPlaceholder = encodeURIComponent(`{${key}}`);
        finalHref = finalHref
            .replaceAll(`:${key}`, value)
            .replaceAll(`{${key}}`, value)
            .replaceAll(encodedPlaceholder, encodeURIComponent(value));
    });

    return finalHref;
}

export async function executeHateoasAction<T = any>({
    action,
    request,
    body,
    pathParams,
    returnHeaders = false,
    mode = 'resources',
    currentPathname,
    onUpdateScope,
    onRefreshSession,
    onRedirect,
}: ExecuteHateoasActionOptions): Promise<any> {
    const finalHref = resolveHateoasHrefTemplate(action.href, pathParams);

    const res = await request(finalHref, {
        method: action.method,
        body: body ?? undefined,
    }, returnHeaders);

    const dataToParse = returnHeaders ? res?.data : res;
    const parsed = HateoasResponseSchema.safeParse(dataToParse);

    if (!dataToParse || typeof dataToParse !== 'object' || (!('resources' in dataToParse) && !('_links' in dataToParse) && !('_actions' in dataToParse) && !('ui' in dataToParse)) || !parsed.success) {
        return res;
    }

    const parsedData = parsed.data;
    const shouldReturnEnvelope = mode === 'envelope';

    const hasActions = parsedData._actions && Object.keys(parsedData._actions).length > 0;
    const hasLinks = parsedData._links && Object.keys(parsedData._links).length > 0;

    if (!parsedData.ui?.effects) {
        if (hasActions || hasLinks) {
            onUpdateScope?.({ actions: parsedData._actions, links: parsedData._links }, action);
        }
        if (shouldReturnEnvelope) {
            return parsedData;
        }
        return parsedData.resources as Record<string, T>;
    }

    const { effects, target, session } = parsedData.ui;

    if (effects.includes('session') && session && typeof window !== 'undefined') {
        if (session.clear && Array.isArray(session.clear)) {
            session.clear.forEach((key) => sessionStorage.removeItem(key));
        }

        if (session.set) {
            Object.entries(session.set).forEach(([key, valueSpec]) => {
                const value = resolveSessionValue(valueSpec, parsedData.resources);

                if (value !== null) {
                    sessionStorage.setItem(key, value);
                } else {
                    console.warn(`Impossible de resoudre la valeur pour la cle "${key}"`);
                }
            });
        }
    }

    if (effects.includes('refresh_session') || effects.includes('clear_session')) {
        await onRefreshSession?.();
    }

    if (hasActions || hasLinks) {
        onUpdateScope?.({ actions: parsedData._actions, links: parsedData._links }, action);
    }

    const cleanTarget = target?.replace(/\/$/, '');
    const cleanPathname = currentPathname?.replace(/\/$/, '');
    if (effects.includes('redirect') && cleanTarget && cleanTarget !== cleanPathname) {
        onRedirect?.(parsedData.ui?.target || '/');
    }

    if (shouldReturnEnvelope) {
        return parsedData;
    }

    return parsedData.resources as Record<string, T>;
}

/**
 * Resolveur de valeur de session pour les specs HATEOAS de type:
 * - valeur brute: "foo"
 * - chemin ressource: { path: "a.b.c" }
 */
export function resolveSessionValue(
    spec: string | { path: string },
    resources: unknown,
): string | null {
    if (typeof spec === 'string') {
        return spec;
    }

    if (spec && typeof spec === 'object' && 'path' in spec) {
        const path = spec.path.split('.');
        let current = resources;
        for (const segment of path) {
            if (current && typeof current === 'object' && segment in current) {
                current = (current as Record<string, unknown>)[segment];
            } else {
                return null;
            }
        }
        return current !== undefined && current !== null ? String(current) : null;
    }

    return null;
}
